// client/src/api/ApiPayments.js
import flex from './FlexFetch'
import { unwrapStrictData, unwrapStrictError } from './contractUtils'

const payments_endpoint = '/api/1.0/payments/'

function isStrictSuccessPayload(payload) {
  return (
    payload?.success === true &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  )
}

function getErrorMessage(error, defaultMessage) {
  const payloadError = unwrapStrictError(error.response?.data)
  if (typeof payloadError === 'string') return payloadError
  return payloadError?.message || error.message || defaultMessage
}

/**
 * Helper function to standardize error handling
 * @throws {Error} Always throws - never returns
 */
const handleApiError = (error, defaultMessage) => {
  const errorMessage = getErrorMessage(error, defaultMessage)
  throw new Error(errorMessage)
}

function normalizeCreateIntentResponse(payload) {
  return unwrapStrictData(payload)
}

function normalizeConfirmAndRegisterResponse(payload) {
  if (!isStrictSuccessPayload(payload)) {
    return payload
  }

  return {
    success: true,
    ...unwrapStrictData(payload),
  }
}

/**
 * Create a PaymentIntent for league registration
 * Note: Access codes are not needed here - they're validated during confirmAndRegister
 * @param {number} leagueId - The ID of the league to register for
 * @returns {Promise<{clientSecret: string, paymentIntentId: string, amount: number}>}
 */
async function createIntent(leagueId) {
  try {
    const payload = { leagueId }

    const response = await flex.flexFetch(payments_endpoint + 'create-intent', {
      method: 'POST',
      body: JSON.stringify(payload),
      credentials: 'include',
    })
    return normalizeCreateIntentResponse(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to create payment intent')
  }
}

/**
 * Confirm payment and complete registration
 * @param {string} paymentIntentId - The Stripe PaymentIntent ID
 * @param {Object} registrationData - Registration details
 * @param {number} registrationData.player_id - Player ID
 * @param {number} registrationData.league_id - League ID
 * @param {string} [registrationData.skill_level] - Player skill level
 * @param {string} [registrationData.access_code] - Optional access/promo code
 * @param {number} [registrationData.partner_player_id] - Doubles partner player ID
 * @returns {Promise<{success: boolean, registration: Object, payment: Object}>}
 */
async function confirmAndRegister(paymentIntentId, registrationData) {
  try {
    const response = await flex.flexFetch(
      payments_endpoint + 'confirm-and-register',
      {
        method: 'POST',
        body: JSON.stringify({
          paymentIntentId,
          ...registrationData,
        }),
        credentials: 'include',
      },
    )
    return normalizeConfirmAndRegisterResponse(
      await flex.handleResponse(response),
    )
  } catch (error) {
    handleApiError(error, 'Failed to confirm payment and register')
  }
}

const ApiPayments = {
  createIntent,
  confirmAndRegister,
}

export default ApiPayments
